import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, Users, DollarSign, Activity, ArrowUpRight, Plus, Sparkles, CheckCircle2, Circle, Wand2, X, Loader2, Store, RefreshCw } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="bg-white border border-zinc-200 rounded-md p-5 hover:shadow-sm transition-all" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
      <Icon className="size-4 text-zinc-400" strokeWidth={1.5} />
    </div>
    <div className="font-display text-3xl font-bold mt-3 tabular">{value}</div>
    {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
  </div>
);

const WizardStep = ({ done, label, action, testid }) => (
  <div className="flex items-center justify-between py-3 px-4 hover:bg-zinc-50 transition-colors" data-testid={testid}>
    <div className="flex items-center gap-3">
      {done ? <CheckCircle2 className="size-5 text-emerald-600" strokeWidth={1.5} /> : <Circle className="size-5 text-zinc-300" strokeWidth={1.5} />}
      <span className={`text-sm ${done ? "text-zinc-400 line-through" : "text-zinc-950 font-medium"}`}>{label}</span>
    </div>
    {!done && action}
  </div>
);

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState({ total_giveaways: 0, active_giveaways: 0, total_entries: 0, tracked_revenue: 0 });
  const [giveaways, setGiveaways] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const reconnect = async () => {
    const shop = user?.shopify_store;
    if (!shop) { window.location.href = "/integrations"; return; }
    setReconnecting(true);
    try {
      const { data } = await api.get(`/shopify/install?shop=${encodeURIComponent(shop)}`);
      const url = data.install_url;
      if (window.top === window.self) window.location.href = url;
      else window.open(url, "_top");
      // Reset if navigation didn't take over (e.g. popup blocked in embedded mode).
      setTimeout(() => setReconnecting(false), 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start reconnect");
      setReconnecting(false);
    }
  };

  const load = async () => {
    const [s, g] = await Promise.all([api.get("/dashboard/stats"), api.get("/giveaways")]);
    setStats(s.data);
    setGiveaways(g.data);
  };

  useEffect(() => { load(); }, []);

  const demoGiveaway = giveaways.find((g) => g.is_demo);

  const loadDemo = async () => {
    setDemoLoading(true);
    try {
      await api.post("/giveaways/seed-demo");
      await load();
      toast.success("Demo data loaded — explore a fully-populated giveaway");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load demo data");
    } finally {
      setDemoLoading(false);
    }
  };

  const clearDemo = async () => {
    setDemoLoading(true);
    try {
      await api.post("/giveaways/clear-demo");
      await load();
      toast.success("Demo data cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear demo data");
    } finally {
      setDemoLoading(false);
    }
  };

  const featured = giveaways[0];
  const wizard = [
    { done: !!user?.shopify_connected, label: "Connect Shopify", to: "/integrations", testid: "wizard-shopify" },
    { done: giveaways.length > 0, label: "Create Giveaway", to: "/giveaways/new", testid: "wizard-create" },
    { done: giveaways.length > 0, label: "Set Entry Ratio", to: featured ? `/giveaways/${featured.giveaway_id}` : "/giveaways/new", testid: "wizard-ratio" },
    { done: stats.tracked_revenue > 0, label: "Import Orders", to: featured ? `/giveaways/${featured.giveaway_id}` : "/giveaways", testid: "wizard-import" },
    { done: giveaways.some((g) => g.status === "drawn"), label: "Pick Winner", to: featured ? `/giveaways/${featured.giveaway_id}` : "/giveaways", testid: "wizard-winner" },
  ];
  const completed = wizard.filter((w) => w.done).length;
  const showWizard = completed < wizard.length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-8 py-10" data-testid="dashboard-root">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Overview</div>
            <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">Dashboard</h1>
          </div>
          <Link
            to="/giveaways/new"
            data-testid="dashboard-create-giveaway"
            className="bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors flex items-center gap-2"
          >
            <Plus className="size-4" strokeWidth={1.5} /> New Giveaway
          </Link>
        </div>

        {/* Shopify connection status banner */}
        {!loading && (user?.shopify_connected && user?.shopify_store ? (
          <div className="mb-6 flex items-center justify-between gap-4 bg-emerald-50 border border-emerald-200 rounded-md px-5 py-3.5" data-testid="store-connected-banner">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-900 bg-emerald-200 px-2 py-1 rounded whitespace-nowrap">
                <Store className="size-3" strokeWidth={2} /> Connected
              </span>
              <span className="text-sm text-emerald-900 truncate">
                Store connected: <span className="font-semibold" data-testid="connected-store-domain">{user.shopify_store}</span>
              </span>
            </div>
            <button
              onClick={reconnect}
              disabled={reconnecting}
              data-testid="reconnect-store-button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-900 border border-emerald-300 px-3 py-1.5 rounded-md hover:bg-emerald-100 disabled:opacity-50 whitespace-nowrap"
            >
              {reconnecting ? <Loader2 className="size-3.5 animate-spin" strokeWidth={2} /> : <RefreshCw className="size-3.5" strokeWidth={2} />} Reconnect
            </button>
          </div>
        ) : (
          <div className="mb-6 flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-md px-5 py-3.5" data-testid="store-disconnected-banner">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-amber-900 bg-amber-200 px-2 py-1 rounded whitespace-nowrap">
                <Store className="size-3" strokeWidth={2} /> Not connected
              </span>
              <span className="text-sm text-amber-900">No Shopify store connected — orders won't convert to entries yet.</span>
            </div>
            <Link
              to="/integrations"
              data-testid="connect-store-link"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 border border-amber-300 px-3 py-1.5 rounded-md hover:bg-amber-100 whitespace-nowrap"
            >
              Connect store →
            </Link>
          </div>
        ))}

        {demoGiveaway && (
          <div className="mb-6 flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-md px-5 py-3.5" data-testid="demo-banner">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-amber-900 bg-amber-200 px-2 py-1 rounded">
                <Sparkles className="size-3" strokeWidth={2} /> Demo
              </span>
              <span className="text-sm text-amber-900">You're viewing sample data. Numbers below are simulated — clear it anytime.</span>
            </div>
            <button
              onClick={clearDemo}
              disabled={demoLoading}
              data-testid="clear-demo-button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 border border-amber-300 px-3 py-1.5 rounded-md hover:bg-amber-100 disabled:opacity-50"
            >
              {demoLoading ? <Loader2 className="size-3.5 animate-spin" strokeWidth={2} /> : <X className="size-3.5" strokeWidth={2} />} Clear demo data
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Trophy} label="Total giveaways" value={stats.total_giveaways} testid="stat-total-giveaways" />
          <StatCard icon={Activity} label="Active" value={stats.active_giveaways} testid="stat-active" />
          <StatCard icon={Users} label="Total entries" value={stats.total_entries.toLocaleString()} testid="stat-entries" />
          <StatCard icon={DollarSign} label="Tracked revenue" value={`$${stats.tracked_revenue.toLocaleString()}`} hint="From purchase entries" testid="stat-revenue" />
        </div>

        {/* Plan usage — monthly orders vs. tier limit */}
        {stats.plan && (
          <div className="mt-4 bg-white border border-zinc-200 rounded-md p-5" data-testid="plan-usage-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Plan usage · this month</div>
                <div className="font-display text-lg font-bold mt-1">
                  <span className="capitalize">{stats.plan}</span> plan
                  <span className="text-zinc-400 font-normal text-sm ml-2">
                    {stats.orders_this_month.toLocaleString()} / {stats.order_limit === null || stats.order_limit === undefined ? "Unlimited" : stats.order_limit.toLocaleString()} orders
                  </span>
                </div>
              </div>
              <Link to="/billing" className="text-xs font-medium text-zinc-700 hover:text-zinc-950 border border-zinc-200 px-3 py-1.5 rounded-md hover:bg-zinc-50" data-testid="plan-usage-upgrade">Manage plan →</Link>
            </div>
            {stats.order_limit ? (
              <>
                <div className="mt-3 h-2 w-full bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${stats.orders_this_month >= stats.order_limit ? "bg-red-500" : stats.orders_this_month / stats.order_limit >= 0.8 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(100, (stats.orders_this_month / stats.order_limit) * 100)}%` }}
                    data-testid="plan-usage-bar"
                  />
                </div>
                {stats.orders_this_month >= stats.order_limit && (
                  <div className="mt-2 text-xs text-red-600" data-testid="plan-usage-warning">You've hit your monthly order limit — upgrade to keep tracking new orders.</div>
                )}
              </>
            ) : (
              <div className="mt-2 text-xs text-emerald-600">Unlimited orders included on this plan.</div>
            )}
          </div>
        )}

        {showWizard && (
          <div className="mt-8 bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="setup-wizard">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Get started</div>
                <h2 className="font-display text-xl font-bold mt-1">Setup checklist</h2>
              </div>
              <div className="text-sm tabular text-zinc-600" data-testid="wizard-progress">Progress: {completed}/{wizard.length} Complete</div>
            </div>
            <div className="divide-y divide-zinc-100">
              {wizard.map((w) => (
                <WizardStep key={w.label} done={w.done} label={w.label} testid={w.testid}
                  action={<Link to={w.to} className="text-xs font-medium text-zinc-700 hover:text-zinc-950 border border-zinc-200 px-3 py-1.5 rounded-md hover:bg-zinc-50">Start →</Link>}
                />
              ))}
            </div>
          </div>
        )}

        {/* Giveaway Snapshot — shareable card */}
        {featured && stats.tracked_revenue > 0 && (
          <div className="mt-6 rounded-md overflow-hidden border border-zinc-950" data-testid="giveaway-snapshot">
            <div className="bg-zinc-950 text-white p-6 grid sm:grid-cols-5 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Revenue</div>
                <div className="font-display text-2xl font-bold tabular mt-1">${stats.tracked_revenue.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Prize Value</div>
                <div className="font-display text-2xl font-bold tabular mt-1">${featured.prize_value.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Multiplier</div>
                <div className="font-display text-2xl font-bold tabular mt-1">{(stats.tracked_revenue / Math.max(featured.prize_value, 1)).toFixed(2)}x</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Status</div>
                <div className="font-display text-2xl font-bold mt-1 text-emerald-400">{(stats.tracked_revenue / Math.max(featured.prize_value, 1)) >= 3 ? "Strong" : "Healthy"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Projected Final</div>
                <div className="font-display text-2xl font-bold tabular mt-1 text-emerald-400">${(stats.tracked_revenue * 1.7).toLocaleString(undefined,{maximumFractionDigits:0})}</div>
              </div>
            </div>
          </div>
        )}

        {/* Health Center promo */}
        <div className="mt-8 relative success-glow bg-white border border-zinc-200 rounded-md p-6 overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="size-4 text-emerald-600" strokeWidth={1.5} />
                <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">Giveaway Health Center</span>
              </div>
              <h2 className="font-display text-2xl font-bold mt-2">How is my giveaway performing?</h2>
              <p className="text-sm text-zinc-600 mt-2 max-w-lg">
                See predicted revenue, projected ROAS, profit estimate, and a real-time health score for any campaign — before and during launch.
              </p>
            </div>
            <Link
              to="/success-score"
              data-testid="dashboard-success-score-cta"
              className="bg-emerald-500 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center gap-2"
            >
              Open Health Center <ArrowUpRight className="size-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        {/* Recent giveaways */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Recent Giveaways</h2>
            <Link to="/giveaways" className="text-sm text-zinc-600 hover:text-zinc-950" data-testid="view-all-giveaways">View all →</Link>
          </div>
          {giveaways.length === 0 ? (
            <div className="border border-dashed border-zinc-200 rounded-md p-12 text-center bg-white">
              <Trophy className="size-8 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-display font-semibold text-lg">No giveaways yet</h3>
              <p className="text-sm text-zinc-500 mt-1">Create your first sweepstakes in under 60 seconds.</p>
              <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                <Link
                  to="/giveaways/new"
                  data-testid="empty-create-giveaway"
                  className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"
                >
                  <Plus className="size-4" strokeWidth={1.5} /> Create giveaway
                </Link>
                <button
                  onClick={loadDemo}
                  disabled={demoLoading}
                  data-testid="load-demo-button"
                  className="inline-flex items-center gap-2 border border-zinc-200 px-4 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors disabled:opacity-50"
                >
                  {demoLoading ? <Loader2 className="size-4 animate-spin" strokeWidth={1.5} /> : <Wand2 className="size-4" strokeWidth={1.5} />} Load demo data
                </button>
              </div>
              <p className="text-xs text-zinc-400 mt-3">Not sure where to start? Load a sample giveaway to explore SweepLaunch.</p>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Title</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Prize</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Entries</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {giveaways.slice(0, 5).map((g) => (
                    <tr key={g.giveaway_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-5 py-4 font-medium">
                        {g.title}
                        {g.is_demo && <span className="ml-2 text-[9px] uppercase tracking-[0.15em] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded align-middle">Demo</span>}
                      </td>
                      <td className="px-5 py-4 tabular">${g.prize_value.toLocaleString()}</td>
                      <td className="px-5 py-4 tabular">{g.total_entries}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${
                          g.status === "active" ? "bg-emerald-50 text-emerald-700" :
                          g.status === "drawn" ? "bg-zinc-100 text-zinc-700" : "bg-zinc-50 text-zinc-600"
                        }`}>
                          <span className={`size-1.5 rounded-full ${g.status === "active" ? "bg-emerald-500" : "bg-zinc-400"}`} />
                          {g.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Link to={`/giveaways/${g.giveaway_id}`} className="text-zinc-600 hover:text-zinc-950 text-xs font-medium" data-testid={`view-giveaway-${g.giveaway_id}`}>
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
